import {
  decideShareAccessByToken,
  ShareAccessPermission,
} from '@app/drive/share-access/share-access-api';
import {Notification} from '@app/gen/schemas/notification';
import {NotificationData} from '@app/gen/schemas/notification-data';
import {showHttpErrorToast} from '@common/http/errors/show-http-error-toast';
import {
  NotificationListItem,
  NotificationListItemProps,
} from '@common/notifications/notification-list';
import {NotificationsDialogContext} from '@common/notifications/notifications-dialog';
import {notificationsBaseKey} from '@common/notifications/notifications-queries';
import {queryClient} from '@common/http/query-client';
import {toast} from '@shadcn/toast/toast';
import {useMutation} from '@tanstack/react-query';
import {Trans} from '@ui/i18n/trans';
import {use} from 'react';
import {FileTypeIcon} from '@common/uploads/components/file-type-icon/file-type-icon';
import {useNavigate} from '@common/ui/navigation/use-navigate';

type ShareAccessNotification = Omit<Notification, 'data'> & {
  data: NotificationData & {requestToken?: string; requestId?: number};
};

export function ShareAccessNotificationRenderer(
  props: NotificationListItemProps,
) {
  const {notification} = props;
  const ctx = use(NotificationsDialogContext);
  const navigate = useNavigate();
  const decide = useMutation({
    mutationFn: (payload: {
      token: string;
      action: 'approve' | 'deny';
      permission?: ShareAccessPermission;
    }) =>
      decideShareAccessByToken(payload.token, {
        action: payload.action,
        permission: payload.permission,
      }),
  });

  return (
    <NotificationListItem
      {...props}
      lineIconRenderer={IconRenderer}
      onActionButtonClick={(e, {action, emitOnly}) => {
        e.stopPropagation();
        const data = (notification as ShareAccessNotification).data;
        if (!emitOnly) {
          if (action) {
            navigate(action);
            ctx.setOpen(false);
          }
          return;
        }
        if (!data.requestToken) {
          return;
        }
        if (action === 'deny') {
          decide.mutate(
            {token: data.requestToken, action: 'deny'},
            {
              onSuccess: () => {
                toast.success(<Trans message="Request declined" />);
                queryClient.invalidateQueries({
                  queryKey: notificationsBaseKey,
                });
                ctx.setOpen(false);
              },
              onError: err => showHttpErrorToast(err),
            },
          );
          return;
        }
        if (action.startsWith('approve:')) {
          const permission = action.split(
            ':',
          )[1] as ShareAccessPermission;
          decide.mutate(
            {
              token: data.requestToken,
              action: 'approve',
              permission,
            },
            {
              onSuccess: () => {
                toast.success(<Trans message="Access granted" />);
                queryClient.invalidateQueries({
                  queryKey: notificationsBaseKey,
                });
                ctx.setOpen(false);
              },
              onError: err => showHttpErrorToast(err),
            },
          );
        }
      }}
    />
  );
}

function IconRenderer({icon}: {icon: string}) {
  return <FileTypeIcon className="h-4 w-4" type={icon} />;
}
